import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TimePointRepresentation } from '@common/api';
import { timePointsApi } from '@data/rest/api';
import { useInvalidateEntityFixtures } from '@data/entity/fixtures';
import { QueryOptions } from '@util/query';
import { useAuth } from '@providers/AuthProvider';

const QUERY_KEY = ['timePoints'];

function getTimePoints() {
    return timePointsApi.list();
}

export function useTimePoints<TSelectData = TimePointRepresentation[]>(
    options?: QueryOptions<TimePointRepresentation[], TSelectData>
) {
    return useQuery(QUERY_KEY, getTimePoints, { staleTime: 5000, ...options });
}

export function useYearsActions() {
    const queryClient = useQueryClient();
    const { invalidateCurrentUserSettings } = useAuth();
    const invalidateEntityFixtures = useInvalidateEntityFixtures();

    const add = useMutation((year: number) => timePointsApi.createYear({ year }), {
        onSettled: async () => {
            await queryClient.invalidateQueries(QUERY_KEY);
            await invalidateEntityFixtures();
        },
    });

    const update = useMutation(
        ({ year, active }: { year: number; active: boolean }) => timePointsApi.updateYear(year, { active }),
        {
            onSettled: async () => {
                await queryClient.invalidateQueries(QUERY_KEY);
                await invalidateEntityFixtures();
                await invalidateCurrentUserSettings();
            },
        }
    );

    const remove = useMutation((year: number) => timePointsApi.removeYear(year), {
        onSettled: async () => {
            await queryClient.invalidateQueries(QUERY_KEY);
            await invalidateEntityFixtures();
        },
    });

    return {
        createYear: add.mutateAsync,
        updateYear: update.mutateAsync,
        deleteYear: remove.mutateAsync,
    };
}
